import { Link } from "react-router-dom";
import { Building2, Warehouse, LandPlot, Factory, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const portfolio = [
  { icon: Warehouse, title: "Industrial Warehousing", desc: "Class A and B logistics warehousing positioned along high-volume freight corridors." },
  { icon: Factory, title: "Light Industrial", desc: "Flex industrial assets serving manufacturers, distributors and government tenants." },
  { icon: LandPlot, title: "Land & Development", desc: "Strategic land holdings prepared for industrial, commercial and infrastructure development." },
  { icon: Building2, title: "Commercial Lease", desc: "Long-term leased commercial properties with credit-quality occupants and stabilized yields." },
];

const principles = [
  { k: "Asset Discipline", v: "Acquire only assets that compound." },
  { k: "Operational Rigor", v: "In-house property and tenant operations." },
  { k: "Community Impact", v: "Development that elevates the neighborhood." },
];

export default function Properties() {
  useSEO({
    title: "Tandem T Properties — Industrial Real Estate",
    description: "Tandem T Properties — industrial and commercial real estate for logistics tenants, federal operators, and the communities we serve. A division of Tandem T Holdings LLC.",
    path: "/properties",
  });
  return (
    <div data-testid="page-properties">
      <PageHero
        testId="properties-hero"
        overline="Division 02 — Tandem T Properties"
        title={<>Industrial real estate, <em className="italic">institutionally operated.</em></>}
        subtitle="An industrial and commercial real estate platform building durable infrastructure for logistics tenants, federal operators and the communities that surround them."
        image="https://images.unsplash.com/photo-1557761469-f29c6e201784"
      />

      {/* PORTFOLIO */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-white/55">Portfolio Focus</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-5xl mt-4 leading-tight">
              Built to support the supply chains that move America.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {portfolio.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 0.08} className="bg-black">
                  <div className="p-10 md:p-14 min-h-[280px] flex flex-col hover:bg-white/[0.03] transition-colors">
                    <Icon size={22} className="text-white" />
                    <h3 className="font-serif font-light text-white text-3xl md:text-4xl mt-8">{p.title}</h3>
                    <p className="text-white/65 font-light leading-relaxed mt-4 max-w-md">{p.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="py-24 md:py-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <p className="overline text-white/55">Investment Doctrine</p>
            <h2 className="font-serif font-light text-white text-3xl md:text-5xl mt-4 leading-tight">
              Three principles. Zero compromise.
            </h2>
            <p className="text-white/65 font-light leading-relaxed mt-6 max-w-md">
              We treat every property as a long-duration asset — operated, optimized and held with
              the same discipline our institutional partners expect.
            </p>
          </Reveal>
          <div className="md:col-span-7 grid grid-cols-1 gap-px bg-white/10">
            {principles.map((p, i) => (
              <Reveal key={p.k} delay={i * 0.06} className="bg-black">
                <div className="p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6">
                  <p className="overline text-white/40 w-32">0{i + 1}</p>
                  <p className="font-serif text-white text-2xl md:text-3xl font-light flex-1">{p.k}</p>
                  <p className="text-white/60 font-light md:max-w-xs">{p.v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <Reveal>
            <h3 className="font-serif font-light text-white text-3xl md:text-5xl max-w-2xl leading-tight">
              Looking to lease, partner, or co-develop with Tandem T?
            </h3>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              data-testid="properties-cta-contact"
              to="/contact?intent=properties"
              className="group inline-flex items-center gap-3 px-6 py-4 bg-white text-black overline hover:bg-white/85"
            >
              Discuss Real Estate <ArrowUpRight size={16} className="hover-arrow" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
